import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import _ from "lodash";

import {
  Checkbox,
  CommonModalFooter,
  CommonModalHeader,
  ModalDialog,
  TimePicker,
  useForm,
} from "../../../../../components";
import { getNextAvailableRuleName } from "../../../../../store/schedules";
import "./ScheduleRuleDialog.scss";

export const ScheduleRuleDialog = ({
  modal,
  timeFormat24h,
  scheduleType,
  profileId,
  weekdays,
  schedule,
}) => {
  const intl = useIntl();
  const availableName = useSelector(
    getNextAvailableRuleName(profileId, scheduleType)
  );

  const form = useForm({
    startTime: schedule?.timeStart || "00:00",
    endTime: schedule?.timeEnd || "00:01",
    selectedWeekdays: schedule?.day || [],
  });

  const changeStartTime = (time) => form.set("startTime", time);
  const changeEndTime = (time) => form.set("endTime", time);

  const toggleWeekday = (weekday) => {
    const selectedWeekdays = form.values.selectedWeekdays;

    if (selectedWeekdays.includes(weekday)) {
      form.set(
        "selectedWeekdays",
        selectedWeekdays.filter((day) => day !== weekday)
      );
    } else {
      form.set("selectedWeekdays", [...selectedWeekdays, weekday]);
    }
  };

  const submit = () => {
    const changes = {
      day: form.values.selectedWeekdays,
      timeStart: form.values.startTime,
      timeEnd: form.values.endTime,
    };

    const scheduleToAdd = {
      type: scheduleType,
      profile: profileId,
      enabled: true,
      name: availableName,
      categories: [],
      ...changes,
    };

    const scheduleToEdit = {
      ...schedule,
      ...changes,
    };

    modal.resolve(schedule ? scheduleToEdit : scheduleToAdd);
  };

  const close = () => modal.reject();

  const isFormHasChanges = !_.isEqual(schedule, {
    ...schedule,
    day: form.values.selectedWeekdays,
    timeStart: form.values.startTime,
    timeEnd: form.values.endTime,
  });
  const isFormDisabled = schedule
    ? _.isEmpty(form.values.selectedWeekdays) || !isFormHasChanges
    : _.isEmpty(form.values.selectedWeekdays);

  const headerTranslationId = schedule
    ? `edit_${scheduleType}_schedule_rule`
    : `add_${scheduleType}_schedule_rule`;

  return (
    <ModalDialog
      className={`schedule-rule__dialog add-rule-dialog ${scheduleType}`}
      submitHandler={submit}
      rejectHandler={close}
      disabled={isFormDisabled}
    >
      <CommonModalHeader>
        <FormattedMessage id={headerTranslationId} />
      </CommonModalHeader>
      <ModalDialog.Body>
        <div className={"time-picker-group"}>
          <TimePicker
            label={<FormattedMessage id={"start_time"} />}
            timeFormat24h={timeFormat24h}
            value={form.values.startTime}
            onChange={(time) => changeStartTime(time)}
          />
          <TimePicker
            label={<FormattedMessage id={"end_time"} />}
            timeFormat24h={timeFormat24h}
            value={form.values.endTime}
            onChange={(time) => changeEndTime(time)}
          />
        </div>
        <fieldset className={"checkbox-group"}>
          <legend className={"checkbox-group__label"}>
            <FormattedMessage id={"weekdays"} />
          </legend>
          <div className="checkbox-group__list">
            {weekdays.map((weekday) => (
              <Checkbox
                id={weekday}
                key={weekday}
                label={<FormattedMessage id={weekday} />}
                aria-label={intl.formatMessage({ id: weekday })}
                checked={form.values.selectedWeekdays.includes(weekday)}
                onChange={() => toggleWeekday(weekday)}
              />
            ))}
          </div>
        </fieldset>
      </ModalDialog.Body>
      <CommonModalFooter submitMessage={<FormattedMessage id={"apply"} />} />
    </ModalDialog>
  );
};
